import React, { useState, useRef, useEffect } from 'react';
import { Calendar } from 'lucide-react';

export default function CustomSimpleDateRange({ initStartDate, initEndDate, onChangeDate }) {
    const [isOpen, setIsOpen] = useState(false);
    const [startDate, setStartDate] = useState(initStartDate);
    const [endDate, setEndDate] = useState(initEndDate);

    // 달력 렌더링을 위한 현재 기준 달
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const dropdownRef = useRef(null);

    // 외부 클릭 시 달력 닫기
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 날짜 포맷팅 (YYYY-MM-DD)
    const formatDate = (date) => {
        if (!date) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // 달력 관련 데이터 계산
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    // 날짜 클릭 핸들러
    const handleDateClick = (day) => {
        const selectedDate = new Date(year, month, day);

        // 1. 시작일이 없거나, 이미 둘 다 선택된 상태에서 다시 클릭하면 시작일로 리셋
        if (!startDate || (startDate && endDate)) {
            setStartDate(selectedDate);
            setEndDate(null);
        }
        // 2. 시작일만 선택된 상태에서 클릭했을 때
        else {
            if (selectedDate < startDate) {
                // 시작일보다 이전 날짜를 클릭하면 시작일을 변경
                setStartDate(selectedDate);
            } else {
                // 정상적으로 종료일 선택 후 달력 닫기
                setEndDate(selectedDate);
                setIsOpen(false);

                onChangeDate(startDate, selectedDate)
                // 🔥 이곳에서 부모 컴포넌트로 값을 전달하거나 API를 호출할 수 있습니다.
                // console.log("선택된 기간:", formatDate(startDate), "~", formatDate(selectedDate));
            }
        }
    };

    const isSelected = (day) => {
        const date = new Date(year, month, day).getTime();
        const start = startDate?.getTime();
        const end = endDate?.getTime();
        if (start === date || end === date) return 'bg-emerald-500 text-white font-bold rounded-full';
        if (start && end && date > start && date < end) return 'bg-emerald-50 text-emerald-700';
        return 'hover:bg-slate-100 rounded-full';
    };

    // 화면에 표시될 텍스트
    const displayValue = startDate && endDate
        ? `${formatDate(startDate)} ~ ${formatDate(endDate)}`
        : startDate
            ? `${formatDate(startDate)} ~ 종료일 선택`
            : '기간을 선택하세요';

    return (
        <div className="relative w-fit" ref={dropdownRef}>
            {/* 1. 입력창 (클릭 시 달력 토글) */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100 cursor-pointer w-[250px]"
            >
                <Calendar size={18} className="text-emerald-500" />
                <span className={`text-sm font-semibold outline-none select-none ${startDate ? 'text-slate-700' : 'text-slate-400'}`}>
          {displayValue}
        </span>
            </div>

            {/* 2. 커스텀 달력 팝업 */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-2 p-4 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 w-[280px]">
                    {/* 달력 헤더 (이전/다음 달) */}
                    <div className="flex justify-between items-center mb-4">
                        <button
                            onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
                            className="text-slate-400 hover:text-slate-700 p-1"
                        >
                            ◀
                        </button>
                        <span className="font-bold text-slate-700">{year}년 {month + 1}월</span>
                        <button
                            onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
                            className="text-slate-400 hover:text-slate-700 p-1"
                        >
                            ▶
                        </button>
                    </div>

                    {/* 달력 요일 */}
                    <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-400 mb-2">
                        {['일', '월', '화', '수', '목', '금', '토'].map(d => <div key={d}>{d}</div>)}
                    </div>

                    {/* 달력 날짜 그리드 */}
                    <div className="grid grid-cols-7 gap-y-1 text-sm text-center">
                        {/* 첫째 주 빈 칸 채우기 */}
                        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                            <div key={`empty-${i}`} />
                        ))}
                        {/* 실제 날짜들 */}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            return (
                                <div
                                    key={day}
                                    onClick={() => handleDateClick(day)}
                                    className={`p-2 cursor-pointer transition-colors ${isSelected(day)}`}
                                >
                                    {day}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}